import {useLocation} from 'react-router'

// One-shot params a server redirect leaves on the address bar for the page to read once; a return
// path that kept them would replay a sign-in event or an error banner on landing.
const TRANSIENT_PARAMS = ['auth-event', 'auth-method', 'trigger', 'auth-error', 'magic-link-sent', 'magic-link-error', 'purchase']

type Here = {pathname: string; search: string; hash: string}

/**
 * The sign-in page's address, carrying the page it was opened from (path, query string, hash) as
 * `?redirectTo=`. The sign-in card posts it back as a form field; the server sanitizes it before
 * it reaches a Location header (`redirectTarget.server.ts`). Opened from home or from a page under
 * /auth/, it carries nothing.
 */
export function signInHref(loginPath: string, here: Here): string {
    if (here.pathname === '/auth' || here.pathname.startsWith('/auth/')) return loginPath
    const params = new URLSearchParams(here.search)
    for (const key of TRANSIENT_PARAMS) params.delete(key)
    const query = params.toString()
    const returnTo = here.pathname + (query ? `?${query}` : '') + here.hash
    // Home is where a sign-in with no return path lands anyway.
    if (returnTo === '/') return loginPath
    return `${loginPath}?${new URLSearchParams({redirectTo: returnTo})}`
}

/**
 * `signInHref` for the page being rendered (`href`, right at SSR), and the same read from the live
 * address bar (`live()`, for the click): pages that rewrite their own address with
 * `history.replaceState` (the intake, the wall's return tokens) move it without the router seeing.
 */
export function useSignInHref(loginPath: string): {href: string; live: () => string} {
    const href = signInHref(loginPath, useLocation())
    return {href, live: () => (typeof window === 'undefined' ? href : signInHref(loginPath, window.location))}
}
